import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth.store';

/**
 * Route-level guard that protects authenticated routes.
 * Redirects unauthenticated users to /login (preserving the intended destination).
 * Shows a loading spinner while user data is being fetched.
 * Renders child routes via <Outlet /> when authenticated with user data.
 *
 * Usage in routes:
 * ```tsx
 * <Route element={<AuthGuard />}>
 *   <Route path="/dashboard" element={<DashboardPage />} />
 * </Route>
 * ```
 */
export function AuthGuard() {
  const { isAuthenticated, isLoading, user, fetchMe } = useAuthStore();
  const location = useLocation();

  useEffect(() => {
    if (isAuthenticated && !user && !isLoading) {
      fetchMe();
    }
  }, [isAuthenticated, user, isLoading, fetchMe]);

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (isAuthenticated && !user) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  return <Outlet />;
}
